import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Filter } from "lucide-react";
import TrainingCourseBox from "../TrainingCourses/content/TrainingCourseBox";
import FilterContent from "../TrainingCourses/content/FilterContent";
import axios from "axios";
import TrainingCourseLoading from "../loading/taining-course-box";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import Breadcrumb from "../Pages/Breadcrumb";
import { useLanguage } from "../../store/language-context";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const SubjectCourses = () => {
  const { URL } = useLanguage();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const searchedKey = searchParams.get("search");
  const searchedPage = searchParams.get("page") || 1;
  const navigate = useNavigate();
  const { id } = useParams(); // Subject ID

  const [seeFilter, setSeeFilter] = useState(false);
  const [date, setDate] = useState({ start: "", end: "" });
  const [selected, setSelected] = useState({
    title: "",
    city: "",
    category: "",
    subject: "",
    dependence: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    title: "",
    city: "",
    category: "",
    subject: "",
    dependence: "",
    start: "",
    end: ""
  });
  
  const [currentPage, setCurrentPage] = useState(searchedPage);
  const buildUrl = (filters) => {
    const params = new URLSearchParams();
    params.append("page", currentPage);
    params.append("per_page", 9);
  
    if (searchedKey) params.append("title", searchedKey);
    if (filters.title) params.append("title", filters.title);
    if (filters.city) params.append("city", filters.city);
    if (filters.category) params.append("category", filters.category);
    if (filters.subject) params.append("subject", filters.subject);
    if (filters.dependence) params.append("dependence", filters.dependence);
    if (filters.start) params.append("start_date", filters.start);
    if (filters.end) params.append("end_date", filters.end);
  
    return `https://e-training.ivorytraining.com/api/ar/courses?${params.toString()}`;
  };
  const urlWithoutFilters = `https://e-training.ivorytraining.com/api/ar/courses?subject=${id}&page=${currentPage}&per_page=9`;

  const baseUrl = `${URL}/courses?subject=${id}&page=${currentPage}&per_page=9`;
  const urlWithSearch = searchedKey ? `${baseUrl}&title=${searchedKey}` : baseUrl;

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['subject-courses', urlWithSearch, URL],
    queryFn: async () => {
      const response = await axios.get(urlWithSearch);
      if (response.status !== 200) throw new Error(t('custom.err'));
      return response?.data?.data;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  // useEffect(() => {
  //   refetch(); // refresh data if searched key or filters change
  // }, [searchedKey, selected.title]);

  const courses = data?.data || [];
  const meta = data?.meta || {};
  const links = data?.links || {};
  const lastPage = meta?.last_page || 1;
  const prevPageUrl = links?.prev;
  const nextPageUrl = links?.next;

  const subjectTitle = courses[0]?.subject?.title || "";
  const categoryImage = courses[0]?.category?.image || "";

  document.title = `${subjectTitle || t('routes.training-plan-details')} | ${t('routes.training-plan')}`;

  const seeFilteredOrPagination = useCallback(
    (page) => {
      setCurrentPage(page);
      navigate(`/subject-courses/${id}?page=${page}`);
    },
    [navigate, id]
  );

  const resetFilter = () => {
    setDate({ start: "", end: "" });
    setSelected({
      title: "",
      city: "",
      category: "",
      subject: "",
      dependence: "",
    });
    setAppliedFilters({
      title: "",
      city: "",
      category: "",
      subject: "",
      dependence: "",
      start: "",
      end: ""
    });
    setSeeFilter(true);
    navigate(`/subject-courses/${id}`);
    setCurrentPage(1);
  };
  
  const paginationNumbers = () => {
    const numbers = [];
    for (let i = 1; i <= lastPage; i++) {
      if (
        i <= 5 ||
        i === currentPage ||
        i === lastPage ||
        (i === currentPage - 1 && currentPage > 3) ||
        (i === currentPage + 1 && currentPage < lastPage)
      ) {
        numbers.push(i);
      }
    }

    const paginationWithDots = [];
    for (let i = 0; i < numbers.length; i++) {
      paginationWithDots.push(numbers[i]);
      if (i < numbers.length - 1 && numbers[i + 1] - numbers[i] > 1) {
        paginationWithDots.push("...");
      }
    }
    return paginationWithDots;
  };
 
  return (
    <>
      <Breadcrumb pages={[t('routes.training-plan'), subjectTitle || t('routes.training-plan-details')]} />

      <main className="container">
        <section className="text-start">
          <button
            className={`filter-btn ${seeFilter ? "opacity-0 max-h-0 max-w-0" : "opacity-100 max-h-full w-auto mb-4"
              }`}
            onClick={() => setSeeFilter((prev) => !prev)}
          >
            <span>
              <Filter size={24} color="white" fill="white" />
            </span>
            <span>{t('training-courses.filter')}</span>
          </button>

          <div className="w-full h-52 bg-cover bg-center rounded-lg mb-6 border overflow-hidden"
            style={{ backgroundImage: `url(${categoryImage || 'https://ivorytraining.com/TrainingPlan/HpWyAyoovWza2p95CdnfYX9clQKB1NODK86Gisjl.jpg'})`, borderRadius: '10px' }}>
            <div className="w-full h-full bg-[#6A2F38]/70 flex items-center justify-center text-white text-xl font-semibold">
              {subjectTitle}
            </div>
          </div>

          <section className="no-pm flex gap-8 items-start max-lg:flex-col">
            <div className={`${seeFilter ? "opacity-100 max-h-full w-[32rem] max-lg:w-full" : "opacity-0 max-h-0 w-0"} transition-all`}>
              <FilterContent
                setSeeFilter={setSeeFilter}
                seeFilter={seeFilter}
                date={date}
                setDate={setDate}
                setSelected={setSelected}
                selected={selected}
                currentPage={currentPage}
                seeFilteredOrPagination={seeFilteredOrPagination}
              >
                <div className="flex justify-center items-center gap-3">
                  <button className="filter-btn" onClick={() => seeFilteredOrPagination(currentPage || 1)}>{t('custom.search')}</button>
                  <button className="filter-btn" onClick={() => resetFilter()}>{t('custom.reset')}</button>
                </div>
              </FilterContent>
            </div>

            <section className={`grid ${seeFilter ? `${error ? 'grid-cols-1' : 'grid-cols-2'} gap-14 max-2xl:grid-cols-1` : `${error ? 'grid-cols-1' : 'grid-cols-3'} gap-14 max-2xl:grid-cols-2 max-lg:gap-14`} py-8 px-3 max-md:grid-cols-1 w-full`}>
              {isLoading && !error ? (
                <TrainingCourseLoading />
              ) : error || courses.length <= 0 ? (
                <div className="flex justify-center items-center h-full w-full text-xl">
                  <p id="err">{t('custom.no-data')}</p>
                </div>
              ) : (
                courses.map((course) => (
                  <TrainingCourseBox key={course.id} course={course} />
                ))
              )}
            </section>
          </section>

          {lastPage > 1 && (
            <div className="flex gap-3 justify-center items-center my-6">
              <button
                className="border-secondary-tw border-2 px-2 py-1 rounded-md hover:ring-1 hover:ring-yellow-200"
                disabled={!prevPageUrl}
                onClick={() => seeFilteredOrPagination(currentPage - 1)}
              >
                <ArrowRight size={20} color="#e08b3d" fill="#e08b3d" />
              </button>

              {paginationNumbers().map((num, index) => (
                <button
                  key={index}
                  className={`mx-1 text-base ${num.toString() === currentPage.toString() ? "text-secondary-tw border-secondary-tw border-2 px-2" : "hover:text-secondary"} rounded-md`}
                  onClick={() => num !== "..." && seeFilteredOrPagination(num)}
                  disabled={num === "..."}
                >
                  {num}
                </button>
              ))}

              <button
                className="border-secondary-tw border-2 px-2 py-1 rounded-md hover:ring-1 hover:ring-yellow-200"
                disabled={!nextPageUrl}
                onClick={() => seeFilteredOrPagination(currentPage + 1)}
              >
                <ArrowLeft size={20} color="#e08b3d" fill="#e08b3d" />
              </button>
            </div>
          )}
        </section>
      </main>
    </>
  );
};

export default SubjectCourses;